import {
    Paper,
    Typography,
    TextField,
    Grid,
    Switch,
    Button,
    FormControlLabel,
    Box,
} from "@mui/material";
import { useState } from "react";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    ResponsiveContainer,
    Legend,
} from "recharts";

import { BudgetProfile, UpdateProfileParams } from "./timeline-section";

type TResourceEditorProps = {
    profile: BudgetProfile;
    onUpdate: (updatedProfile: UpdateProfileParams) => void;
};

type TChartDataPoint = {
    duration: number;
    single: number;
    multiple: number;
};

const ResourceEditor = ({ profile, onUpdate }: TResourceEditorProps) => {
    const [startWeek, setStartWeek] = useState<number>(profile.startWeek);
    const [hoursPerDay, setHoursPerDay] = useState<number>(profile.hoursPerDay);
    const [efficiency, setEfficiency] = useState<number>(
        profile.efficiencyPercentage
    );
    const [additionalCollaborators, setAdditionalCollaborators] =
        useState<number>(profile.additionalCollaborators || 0);
    const [reductionPercentage, setReductionPercentage] = useState<number>(
        profile.reductionPercentage
    );
    const [advancedConfig, setAdvancedConfig] = useState<boolean>(
        profile.additionalCollaborators > 0
    );

    const singleCollaboratorWeeks = Math.ceil(
        profile.totals.totalHours / (hoursPerDay * 5)
    );
    const singleCollaboratorCost =
        Number(profile.costPerHour) * profile.totals.totalHours;

    const multipleCollaboratorsWeeks = Math.ceil(
        profile.totals.totalHours /
            (hoursPerDay *
                5 *
                (additionalCollaborators + 1) *
                (reductionPercentage / 100))
    );
    const multipleCollaboratorsCost =
        Number(profile.costPerHour) *
        profile.totals.totalHours *
        (additionalCollaborators + 1);

    const chartData: TChartDataPoint[] = Array.from({ length: 31 }, (_, i) => {
        const x = i;
        const singleY =
            i <= singleCollaboratorWeeks
                ? singleCollaboratorCost * (1 - i / singleCollaboratorWeeks)
                : 0;

        const multipleY =
            i <= multipleCollaboratorsWeeks
                ? multipleCollaboratorsCost *
                  (1 - i / multipleCollaboratorsWeeks)
                : 0;

        return {
            duration: x,
            single: Math.round(singleY / 1000) * 1000,
            multiple: Math.round(multipleY / 1000) * 1000,
        };
    });

    const handleSave = () => {
        onUpdate({
            id_e: profile.id_e,
            startWeek,
            hoursPerDay,
            efficiencyPercentage: efficiency,
            additionalCollaborators,
            reductionPercentage,
        });
    };

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat("pt-BR", {
            style: "currency",
            currency: "BRL",
        }).format(value);
    };

    const handleAdvancedConfig = (value: boolean) => {
        setAdvancedConfig(value);
        setAdditionalCollaborators(0);
    };

    return (
        <Paper elevation={1} sx={{ p: 4 }}>
            <Typography variant="h6" gutterBottom>
                Editar Recurso
            </Typography>

            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid size={12}>
                    <TextField
                        fullWidth
                        label="Semana de Início"
                        type="number"
                        value={startWeek}
                        onChange={(e) => setStartWeek(Number(e.target.value))}
                        inputProps={{ min: 1, max: 10 }}
                    />
                </Grid>
                <Grid size={12}>
                    <TextField
                        fullWidth
                        label="Horas Trabalhadas / Dia"
                        type="number"
                        value={hoursPerDay}
                        onChange={(e) => setHoursPerDay(Number(e.target.value))}
                    />
                </Grid>
                <Grid size={12}>
                    <TextField
                        fullWidth
                        label="Eficiência"
                        type="number"
                        value={efficiency}
                        onChange={(e) => setEfficiency(Number(e.target.value))}
                        InputProps={{ endAdornment: "%" }}
                    />
                </Grid>
            </Grid>

            <FormControlLabel
                control={
                    <Switch
                        checked={advancedConfig}
                        onChange={(e) => handleAdvancedConfig(e.target.checked)}
                        color="primary"
                    />
                }
                label="Configuração avançada de time"
                sx={{ mb: 3 }}
            />

            <Grid
                container
                spacing={3}
                sx={{ mb: 4, opacity: advancedConfig ? 1 : 0.5 }}
            >
                <Grid size={12}>
                    <TextField
                        fullWidth
                        label="Qtd. de Colaboradores Adicionais"
                        type="number"
                        value={additionalCollaborators}
                        onChange={(e) =>
                            setAdditionalCollaborators(Number(e.target.value))
                        }
                        disabled={!advancedConfig}
                        inputProps={{ min: 0 }}
                    />
                </Grid>
                <Grid size={12}>
                    <TextField
                        fullWidth
                        label="% de Redução"
                        type="number"
                        value={reductionPercentage}
                        onChange={(e) =>
                            setReductionPercentage(Number(e.target.value))
                        }
                        disabled={!advancedConfig}
                        inputProps={{ min: 1, max: 100 }}
                        helperText="Esse valor irá reduzir a duração desse período pela quantidade de colaboradores."
                    />
                </Grid>
            </Grid>

            <Box sx={{ height: "288px", marginBottom: "32px" }}>
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                        data={chartData}
                        margin={{
                            left: 30,
                            bottom: 30,
                        }}
                    >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                            dataKey="duration"
                            label={{
                                value: "Duração",
                                position: "insideBottom",
                                offset: -5,
                            }}
                        />
                        <YAxis
                            label={{
                                value: "Custo",
                                angle: -90,
                                style: { textAnchor: "middle" },
                                dx: -50,
                            }}
                            tickFormatter={(value) =>
                                `${value === 0 ? "0" : `${value / 1000}K`}`
                            }
                        />
                        <Line
                            type="monotone"
                            dataKey="single"
                            stroke="#2563eb"
                            name="1 Colaborador"
                            strokeWidth={2}
                            dot={false}
                        />
                        {advancedConfig && (
                            <Line
                                type="monotone"
                                dataKey="multiple"
                                stroke="#d946ef"
                                name={`${
                                    additionalCollaborators + 1
                                } Colaboradores`}
                                strokeWidth={2}
                                dot={false}
                            />
                        )}
                        <Legend
                            verticalAlign="top"
                            align="right"
                            iconType="circle"
                            iconSize={10}
                            wrapperStyle={{
                                padding: "0 16px",
                                fontSize: "12px",
                            }}
                        />
                    </LineChart>
                </ResponsiveContainer>
            </Box>

            <Grid
                container
                sx={{ justifyContent: "space-between", alignItems: "center" }}
            >
                <Grid>
                    <Grid container spacing={4}>
                        <Grid>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                1 COLABORADOR
                            </Typography>
                            <Typography
                                variant="h6"
                                color="primary"
                                fontWeight="bold"
                            >
                                {formatCurrency(singleCollaboratorCost)}
                            </Typography>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                {singleCollaboratorWeeks} semanas
                            </Typography>
                        </Grid>

                        {advancedConfig && (
                            <Grid>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                >
                                    {additionalCollaborators + 1} COLABORADORES
                                </Typography>
                                <Typography
                                    variant="h6"
                                    color="secondary"
                                    fontWeight="bold"
                                >
                                    {formatCurrency(multipleCollaboratorsCost)}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                >
                                    {multipleCollaboratorsWeeks} semanas
                                </Typography>
                            </Grid>
                        )}
                    </Grid>
                </Grid>

                <Grid>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSave}
                    >
                        SALVAR
                    </Button>
                </Grid>
            </Grid>
        </Paper>
    );
};

export default ResourceEditor;
